export interface ObservabilityContext {
  machineId: string | undefined;
  broadcastId: string | undefined;
}

const HEADER_MACHINE_ID = 'X-Machine-Id';
const HEADER_BROADCAST_ID = 'X-Broadcast-Id';

/**
 * Set X-Machine-Id and X-Broadcast-Id on outbound HTTP headers.
 * `getMachineId` and `getBroadcastId` are injected to avoid coupling
 * to any specific state store. Omits headers when the getter returns
 * undefined so downstream services can distinguish "not known" from "".
 */
export function addObservabilityHeaders(
  headers: Headers,
  getMachineId: () => string | undefined,
  getBroadcastId?: () => string | undefined,
): Headers {
  const machineId = getMachineId();
  if (machineId !== undefined) {
    headers.set(HEADER_MACHINE_ID, machineId);
  }

  const broadcastId = getBroadcastId?.();
  if (broadcastId !== undefined) {
    headers.set(HEADER_BROADCAST_ID, broadcastId);
  }

  return headers;
}

/**
 * Read X-Machine-Id and X-Broadcast-Id from inbound HTTP headers.
 * Handles both plain string values and multi-value arrays (Express passes
 * multi-value headers as `string[]`). Returns undefined for missing headers.
 */
export function extractObservabilityContext(
  headers: Record<string, string | string[] | undefined>,
): ObservabilityContext {
  const raw = (key: string): string | undefined => {
    const val = headers[key] ?? headers[key.toLowerCase()];
    if (val === undefined) return undefined;
    return Array.isArray(val) ? val[0] : val;
  };

  return {
    machineId: raw(HEADER_MACHINE_ID),
    broadcastId: raw(HEADER_BROADCAST_ID),
  };
}
